import { inject, Injectable, Signal } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { UserStateSelectors } from '../../store/users-store/user.selectors';
import { MergedPermission } from '../../store/users-store/user.model';
import { GetUser } from '../../store/users-store/user.actions';
import { firstValueFrom, filter, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
class UserRoles {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  userPermissions$: Signal<MergedPermission[] | []> = this.store.selectSignal(
    UserStateSelectors.getUserPermissions,
  );

  get userPermissions(): MergedPermission[] {
    return this.userPermissions$().length > 0
      ? this.userPermissions$()
      : this.getDefaultPermissions();
  }

  private getDefaultPermissions(): MergedPermission[] {
    return [
      { slug: 'show-menu-product', allowed: true },
      { slug: 'list-product-data', allowed: true },
      { slug: 'scan-product-data', allowed: true },
      { slug: 'list-product-design', allowed: true },
      { slug: 'list-product-qr', allowed: true },
      { slug: 'list-qr-download', allowed: true },
      { slug: 'list-qr-order', allowed: true },
      { slug: 'list-role', allowed: true },
      { slug: 'list-user', allowed: true },
      { slug: 'edit-product', allowed: true },
      { slug: 'edit-product-design', allowed: true },
      { slug: 'edit-product-qr', allowed: true },
      { slug: 'edit-qr-order', allowed: true },
      { slug: 'edit-role', allowed: true },
      { slug: 'edit-user', allowed: true },
      { slug: 'list-cte-kde', allowed: true },
      { slug: 'create-subscription', allowed: true },
      { slug: 'view-subscription', allowed: true },
      { slug: 'show-menu-recall-product', allowed: true },
      { slug: 'show-menu-reporting', allowed: true },
      { slug: 'tracking-dashboard', allowed: true },
      { slug: 'show-menu-dashboard', allowed: true },
    ];
  }
  public navigate() {
    this.router.navigate(['/no-access']);
  }

  async ensureUserLoaded(): Promise<boolean> {
    const user = this.store.selectSnapshot(UserStateSelectors.getUser);

    if (user) {
      return true;
    }

    const token = localStorage.getItem('regen_access_token');
    if (!token) {
      return false;
    }

    try {
      await firstValueFrom(
        this.store.select(UserStateSelectors.getUser).pipe(
          filter((u) => u !== null && u !== undefined),
          timeout(30000),
        ),
      );
      return true;
    } catch {
      const userAfterTimeout = this.store.selectSnapshot(UserStateSelectors.getUser);
      return userAfterTimeout !== null && userAfterTimeout !== undefined;
    }
  }
}
@Injectable({
  providedIn: 'root',
})
class PermissionsService {
  async checkPermissions(currentUser: UserRoles, requiredPermissions: string[]): Promise<boolean> {
    const userLoaded = await currentUser.ensureUserLoaded();

    if (!userLoaded) {
      currentUser.navigate();
      return false;
    }

    const permissions = currentUser.userPermissions;
    const hasPermission = requiredPermissions.some((permission) =>
      permissions.some((p) => p.slug === permission && p.allowed),
    );

    if (!hasPermission) {
      currentUser.navigate();
    }

    return hasPermission;
  }
}
export const canActivateTeam: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const requiredPermissions = route.data['requiredPermissions'] as string[];

  return inject(PermissionsService).checkPermissions(inject(UserRoles), requiredPermissions);
};
